import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CatalogApiService } from '@/services/course.service';
import { CatalogItem } from '@/types/course.types';
import { sanitizeText } from '@/utils/sanitize';
import { SiteMenuService } from '@/services/site-menu.service';
import { adminSearchPages, SearchablePage } from '@/config/searchablePages';
import { canOpenAdminPage } from '@/config/adminAccess';
import { useAuth } from '@/contexts/shared/AuthContext';
import { useMultilang } from '@/hooks/useMultilang';
import styles from './GlobalSearch.module.css';
import barStyles from '../SearchBar/SearchBar.module.css';
import { adminCategoryLabel } from '../Header/SettingsMenu';
import SearchIcon from '../../../../public/assets/icons/search.svg?react';

import { tr } from '@/i18n';
interface GlobalSearchProps {
  /** Applied to the search-bar shell so it inherits the header styling. */
  className?: string;
  placeholder?: string;
}

type Kind = 'course' | 'program' | 'certification';

const kindOf = (item: CatalogItem): Kind => {
  // The catalog endpoint returns `type` (course|program); `entity_type` is a
  // fallback for other callers. Certifications are programs with a cert subtype.
  const t = item.type ?? item.entity_type;
  if (t === 'course') return 'course';
  return (item.program_type || '').toLowerCase() === 'certification'
    ? 'certification'
    : 'program';
};

const titleOf = (item: CatalogItem): string => item.name ?? item.title ?? '';

// `label` is a function so tr() runs at render time, in the active language.
const KIND_META: Record<Kind, { label: () => string; bg: string; fg: string; route: (id: number) => string }> = {
  course:        { label: () => tr("gradebook.col_course", "Course"),        bg: 'rgba(58,160,255,0.16)',  fg: '#69b6ff', route: (id) => `/user/course-start/${id}` },
  program:       { label: () => tr("ui.program", "Program"),       bg: 'rgba(58,160,255,0.16)',  fg: '#69b6ff', route: (id) => `/user/program-start/${id}` },
  certification: { label: () => tr("ui.certification", "Certification"), bg: 'rgba(139,80,234,0.18)',  fg: '#a78bfa', route: (id) => `/user/program-start/${id}` },
};

const ItemIcon: React.FC<{ kind: Kind }> = ({ kind }) => {
  // Simple, theme-safe inline glyphs (no external asset dependency).
  if (kind === 'course') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      </svg>
    );
  }
  if (kind === 'certification') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="8" r="6" /><path d="M8.21 13.89 7 22l5-3 5 3-1.21-8.11" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 10v6M2 10l10-5 10 5-10 5z" /><path d="M6 12v5c3 3 9 3 12 0v-5" />
    </svg>
  );
};

/** Visible name of a page group (site menu or admin section); the group value stays an English id. */
const groupLabel = (group: string): string => (group === 'Menu' ? tr("course.tabs.menu", "Menu") : adminCategoryLabel(group));

export const GlobalSearch: React.FC<GlobalSearchProps> = ({ className, placeholder = tr("ui.search_courses_programs_certifications_list", "Search courses, programs, certifications…") }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<CatalogItem[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapRef = useRef<HTMLDivElement>(null);

  // ── Pages index (navigate to app pages, not just catalog items) ──────────
  const { user } = useAuth();
  const { ml } = useMultilang();
  const isAdmin = !!user?.roles?.some((r) => {
    const n = (r.name || '').toLowerCase();
    return n.includes('superadmin') || n.includes('site_admin') || n.includes('tenant_admin')
      || n === 'tenantadmin' || n.includes('business_admin');
  });

  // The learner sidebar comes from the DB site menu (visibility-filtered by the
  // backend), fetched once. Admin secondary-nav pages come from the static
  // registry, shown only to admins.
  const [menuPages, setMenuPages] = useState<SearchablePage[]>([]);
  useEffect(() => {
    let cancelled = false;
    SiteMenuService.getUserMenu()
      .then((res) => {
        if (cancelled) return;
        const pages = (res.results || [])
          .filter((m) => m.url && m.url.trim())
          .map((m) => ({ label: ml(m.label) || m.label, path: m.url as string, group: 'Menu' }));
        setMenuPages(pages);
      })
      .catch(() => { /* non-fatal — catalog + admin pages still work */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pagesIndex = useMemo<SearchablePage[]>(() => {
    const adminPages = isAdmin ? adminSearchPages().filter((p) => canOpenAdminPage(user, p.path)) : [];
    const combined = [...menuPages, ...adminPages];
    // Dedupe by path (site-menu entry wins over the static admin duplicate).
    const byPath = new Map<string, SearchablePage>();
    for (const p of combined) if (!byPath.has(p.path)) byPath.set(p.path, p);
    return Array.from(byPath.values());
  // `query` is a dependency on purpose: the language bundle may load after mount (DEF-LX-002).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuPages, isAdmin, user, query]);

  const pageResults = useMemo<SearchablePage[]>(() => {
    const needle = query.trim().toLowerCase();
    if (needle.length < 2) return [];
    return pagesIndex
      .filter((p) =>
        p.label.toLowerCase().includes(needle) ||
        p.group.toLowerCase().includes(needle) ||
        groupLabel(p.group).toLowerCase().includes(needle) ||
        (p.keywords ? p.keywords.toLowerCase().includes(needle) : false),
      )
      // Prefer label-prefix matches, then label matches, then the rest.
      .sort((a, b) => {
        const rank = (p: SearchablePage) =>
          p.label.toLowerCase().startsWith(needle) ? 0 : p.label.toLowerCase().includes(needle) ? 1 : 2;
        return rank(a) - rank(b);
      })
      .slice(0, 6);
  }, [query, pagesIndex]);

  // Debounced catalog search (title + description, courses + programs + certs).
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const res = await CatalogApiService.search({ q, limit: 8 });
        setResults(res.items || []);
        setActiveIndex(-1);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [query]);

  // Close on outside click.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  // Close + reset on route change.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const goToItem = useCallback((item: CatalogItem) => {
    setOpen(false);
    setQuery('');
    navigate(KIND_META[kindOf(item)].route(item.id));
  }, [navigate]);

  const goToAll = useCallback(() => {
    const q = query.trim();
    if (!q) return;
    setOpen(false);
    navigate(`/user/courses?q=${encodeURIComponent(q)}`);
  }, [navigate, query]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIndex >= 0 && results[activeIndex]) goToItem(results[activeIndex]);
      else if (results.length === 0 && pageResults.length > 0) {
        // Only page matches — jump straight to the top page.
        setOpen(false); setQuery(''); navigate(pageResults[0].path);
      } else goToAll();
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const q = query.trim();
  const showPanel = open && q.length >= 2;

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <div className={`${barStyles.searchBar} ${className || ''}`}>
        <SearchIcon className={barStyles.searchIcon} />
        <input
          type="text"
          className={barStyles.searchInput}
          placeholder={placeholder}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          aria-label={tr("ui.search_courses_programs_and_certifications", "Search courses, programs and certifications")}
        />
      </div>

      {showPanel && (
        <div className={styles.panel} role="listbox">
          {/* ── Pages (navigation / admin pages) ── */}
          {pageResults.length > 0 && (
            <>
              <div className={styles.sectionLabel}>{tr("ui.pages", "Pages")}</div>
              {pageResults.map((p) => (
                <button
                  key={p.path}
                  type="button"
                  className={styles.item}
                  onClick={() => { setOpen(false); setQuery(''); navigate(p.path); }}
                  role="option"
                >
                  <span className={styles.thumb} style={{ background: 'color-mix(in srgb, var(--accent) 18%, transparent)', color: 'var(--accent-ink)' }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
                      <rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" />
                    </svg>
                  </span>
                  <span className={styles.itemBody}>
                    <div className={styles.itemTitle}>{p.label}</div>
                    <div className={styles.itemMeta}>
                      <span className={styles.itemSub}>{groupLabel(p.group)}</span>
                    </div>
                  </span>
                </button>
              ))}
              {(loading || results.length > 0) && <div className={styles.sectionLabel}>{tr("nav.learning", "Learning")}</div>}
            </>
          )}

          {loading && results.length === 0 && (
            <div className={styles.state}>{tr("tags.merge.searching", "Searching…")}</div>
          )}
          {!loading && results.length === 0 && pageResults.length === 0 && (
            <div className={styles.state}>{tr("ui.no_results_for", "No results for “")}{q}”.</div>
          )}
          {results.length > 0 && (
            <>
              {results.map((item, idx) => {
                const kind = kindOf(item);
                const meta = KIND_META[kind];
                return (
                  <button
                    key={`${item.type ?? item.entity_type}-${item.id}`}
                    type="button"
                    className={`${styles.item} ${idx === activeIndex ? styles.itemActive : ''}`}
                    onMouseEnter={() => setActiveIndex(idx)}
                    onClick={() => goToItem(item)}
                    role="option"
                    aria-selected={idx === activeIndex}
                  >
                    <span className={styles.thumb} style={{ background: meta.bg, color: meta.fg }}>
                      <ItemIcon kind={kind} />
                    </span>
                    <span className={styles.itemBody}>
                      <div className={styles.itemTitle}>{titleOf(item)}</div>
                      <div className={styles.itemMeta}>
                        <span className={styles.badge} style={{ background: meta.bg, color: meta.fg }}>
                          {meta.label()}
                        </span>
                        {item.summary && (
                          <span className={styles.itemSub}>{sanitizeText(item.summary)}</span>
                        )}
                      </div>
                    </span>
                  </button>
                );
              })}
              <button type="button" className={styles.footer} onClick={goToAll}>
                {tr("ui.see_all_results_for", "See all results for “")}{q}”
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
};
